// src/pages/LoginPage.js
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

export default function LoginPage() {
  const { login, loginWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [resetMode, setResetMode] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    try {
      await loginWithGoogle();
      toast.success("Logged in with Google!");
      navigate("/dashboard");
    } catch (err) {
      console.error("Google login error:", err.code, err.message);
      const errorMap = {
        "auth/popup-closed-by-user": "You closed the login popup.",
        "auth/popup-blocked": "Popup was blocked. Please allow popups.",
        "auth/unauthorized-domain": "Domain not authorized for Google login.",
        "auth/operation-not-allowed": "Google sign-in is not enabled.",
      };
      toast.error(errorMap[err.code] || "Google login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email first"); return; }
    setLoading(true);
    try {
      await resetPassword(email);
      toast.success("Password reset email sent!");
      setResetMode(false);
    } catch {
      toast.error("Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 40, marginBottom: 6 }}>🏠</div>
          <h1 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 24, color: "#1a6b3c" }}>
            Apna<span style={{ color: "#f59e0b" }}>Satna</span>
          </h1>
          <p style={{ color: "#6b7c6b", fontSize: 14, marginTop: 4 }}>
            {resetMode ? "Reset your password" : "Welcome back! Log in to continue"}
          </p>
        </div>

        <div style={{ background: "white", borderRadius: 16, padding: "28px 24px", border: "1px solid #e2ece2", boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
          <form onSubmit={resetMode ? handleReset : handleLogin}>
            <Field label="Email Address">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="yourname@email.com" required style={INPUT} />
            </Field>
            {!resetMode && (
              <Field label="Password">
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required style={INPUT} />
              </Field>
            )}
            <button type="submit" disabled={loading} style={{
              width: "100%", padding: "13px", borderRadius: 9,
              background: loading ? "#a8d5b9" : "#1a6b3c", color: "white",
              border: "none", fontWeight: 700, fontSize: 15,
              cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit", marginTop: 4
            }}>
              {loading ? "Please wait..." : resetMode ? "Send Reset Link" : "Login →"}
            </button>
          </form>

          {!resetMode && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0" }}>
                <div style={{ flex: 1, height: 1, background: "#e2ece2" }} />
                <span style={{ fontSize: 12, color: "#6b7c6b" }}>OR</span>
                <div style={{ flex: 1, height: 1, background: "#e2ece2" }} />
              </div>
              <button onClick={handleGoogle} disabled={loading} style={{
                width: "100%", padding: "12px", borderRadius: 9,
                background: "white", color: "#1a2e1a", border: "1.5px solid #e2ece2",
                fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Continue with Google
              </button>
            </>
          )}

          <div style={{ textAlign: "center", marginTop: 16, fontSize: 13 }}>
            <button onClick={() => setResetMode(r => !r)} style={{ background: "none", border: "none", color: "#1a6b3c", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600 }}>
              {resetMode ? "← Back to login" : "Forgot password?"}
            </button>
          </div>
        </div>

        <p style={{ textAlign: "center", marginTop: 18, fontSize: 14, color: "#6b7c6b" }}>
          New here? <Link to="/register" style={{ color: "#1a6b3c", fontWeight: 700 }}>Create account →</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: "#1a2e1a", display: "block", marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

const INPUT = {
  width: "100%", padding: "11px 14px", border: "1.5px solid #e2ece2",
  borderRadius: 8, fontFamily: "inherit", fontSize: 14, color: "#1a2e1a", background: "white"
};

function friendlyError(code) {
  const map = {
    "auth/user-not-found":      "No account found with this email.",
    "auth/wrong-password":      "Incorrect password. Try again.",
    "auth/invalid-email":       "Please enter a valid email address.",
    "auth/too-many-requests":   "Too many attempts. Try again later.",
    "auth/invalid-credential":  "Invalid email or password.",
  };
  return map[code] || "Login failed. Please try again.";
}
